import { DatePicker } from '@components/core-ui/form-elements/DatePicker';
import CloseIcon from '@components/icons/Close.svg';
import { useState } from 'react';
import { ButtonsWrapper } from './styles/ButtonsWrapper';
import { FilterTitle } from './styles/FilterTitle';
import { SuggestionButtonsWrapper } from './styles/SuggestionButtonsWrapper';
import { Wrapper } from './styles/Wrapper';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TCreatedFilter } from './types/TCreatedFilter';
import { TProps } from './types/TProps';

const firstDayOfCurrentWeek = () => {
  const dayOfWeek = new Date().getDay();
  const firstDayOfWeek = new Date();
  const diff = dayOfWeek >= 0 ? dayOfWeek : 6 - dayOfWeek;
  firstDayOfWeek.setDate(new Date().getDate() - diff);
  firstDayOfWeek.setHours(0, 0, 0, 0);
  return firstDayOfWeek;
};

const firstDayOfCurrentMonth = () => {
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDate() + 1);
  firstDayOfMonth.setHours(0, 0, 0, 0);
  return firstDayOfMonth;
};

const startOfCurrentDay = () => {
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  return currentDay;
};

const getSelectedDateFilterValue = (jobsFilter: TCreatedFilter): string => {
  let to = jobsFilter.to || 0;
  let from = jobsFilter.from || 0;
  const today = startOfCurrentDay().getTime();
  const thisWeekStart = firstDayOfCurrentWeek().getTime();
  const thisMonthStart = firstDayOfCurrentMonth().getTime();
  if (from) {
    from = parseInt(new Date(from).setHours(0, 0, 0, 0).toString(), 10);
  }
  if (to) {
    to = parseInt(new Date(to).setHours(0, 0, 0, 0).toString(), 10);
  }
  if (from === today && to === today) {
    return 'today';
  }
  if (from === thisWeekStart && to === today) {
    return 'week';
  }
  if (from === thisMonthStart && to === today) {
    return 'month';
  }
  return '';
};

export const Filter = ({
  createdFilter,
  createdFilterOnChange,
  handleSubmit,
  handleClose,
  hideHeader = false,
}: TProps) => {
  const [tempDateFilterSelected, setTempDateFilterSelected] = useState(getSelectedDateFilterValue(createdFilter ?? {}));
  const [tempCreatedFilter, setTempCreatedFilter] = useState<TCreatedFilter>(createdFilter ?? {});
  const tempSubmit = () => {
    if (createdFilterOnChange) {
      createdFilterOnChange(tempCreatedFilter);
    }
    handleSubmit(tempCreatedFilter);
    handleClose();
  };
  const tempClose = () => {
    if (createdFilterOnChange) {
      createdFilterOnChange(createdFilter ?? {});
    }
    handleClose();
  };

  return (
    <Wrapper $compact={hideHeader}>
      {hideHeader ? null : (
        <>
          <FilterTitle>Filters</FilterTitle>
          <div
            className="absolute right-[15px] h-6 w-6 cursor-pointer [&_svg]:h-6 [&_svg]:w-6 [&_svg]:fill-[var(--color-text-muted)]"
            onClick={tempClose}
          >
            <CloseIcon />
          </div>
        </>
      )}

      <div className="label">Created</div>
      <div className="datepicker-wrapper">
        <DatePicker
          label="From"
          minDate={new Date(0)}
          maxDate={tempCreatedFilter.to || new Date()}
          value={tempCreatedFilter.from}
          onChange={(date: Date | null) =>
            setTempCreatedFilter((v: TCreatedFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = date ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <div className="datepicker-wrapper">
        <DatePicker
          label="To"
          minDate={tempCreatedFilter.from}
          maxDate={new Date()}
          value={tempCreatedFilter.to}
          onChange={(date: Date | null) =>
            setTempCreatedFilter((v: TCreatedFilter) => {
              const newVal = Object.assign({}, v);
              let newDate = null;
              if (date) {
                const dateObj = new Date(date);
                dateObj.setHours(23);
                dateObj.setMinutes(59);
                newDate = dateObj;
              }
              newVal.to = newDate ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <SuggestionButtonsWrapper>
        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'today'}
          onClick={() => {
            setTempCreatedFilter((v: TCreatedFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = startOfCurrentDay();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('today');
          }}
        >
          Today
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'week'}
          onClick={() => {
            setTempCreatedFilter((v: TCreatedFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = firstDayOfCurrentWeek();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('week');
          }}
        >
          This week
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'month'}
          onClick={() => {
            setTempCreatedFilter((v: TCreatedFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = firstDayOfCurrentMonth();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('month');
          }}
        >
          This month
        </OutlinedButton>
      </SuggestionButtonsWrapper>

      <ButtonsWrapper>
        <OutlinedButton
          onClick={() => {
            setTempCreatedFilter({
              from: undefined,
              to: undefined,
            });
            setTempDateFilterSelected('');
            const clearedFilter = {
              from: undefined,
              to: undefined,
            };
            if (createdFilterOnChange) {
              createdFilterOnChange(clearedFilter);
            }
            handleSubmit(clearedFilter);
            handleClose();
          }}
        >
          Reset
        </OutlinedButton>
        <Button onClick={tempSubmit}>Apply</Button>
      </ButtonsWrapper>
    </Wrapper>
  );
};
